/**
 * Group chats: who may do what, and what New chat does with what it is given.
 *
 * Pure, so a test can read it. The server decides every one of these
 * (`relay-server/worker/routes/conversations.ts`); this is the screen's copy of
 * the answer, kept so that no control is offered that the server will refuse.
 *
 * **Group chat is the only name.** The wire has two kinds for a conversation of
 * more than two people, `group` and `channel`, and the protocol keeps both. The
 * person sees one thing, so everything that asks "is this a group chat" asks
 * `isGroupChat`.
 */

import { ApiError } from '../../lib/api';
import type { Conversation, ConversationKind, Person, RelayStore } from '../../lib/relay/types';

/**
 * Mauvely's own conversation is drawn as a channel whatever its membership, and
 * is not a group of people: it has two, one of whom only posts.
 */
export function isGroupChat(conversation: Conversation): boolean {
  return !conversation.official && (conversation.kind === 'group' || conversation.kind === 'channel');
}

export interface Powers {
  /** The name and the topic. */
  rename: boolean;
  add: boolean;
  /** Anyone else. */
  remove: boolean;
  leave: boolean;
  /** Delete for everyone. */
  dissolve: boolean;
}

const NOTHING: Powers = { rename: false, add: false, remove: false, leave: false, dissolve: false };

/**
 * What this account may do in a conversation.
 *
 * Any member may add and may leave. Everything else is the owner's: the name,
 * the topic, removing someone, and taking the room down. An owner the server
 * did not name is nobody — an empty id is not "not you", and neither gets the
 * owner's controls.
 */
export function powers(conversation: Conversation, meId: string | undefined): Powers {
  if (!isGroupChat(conversation)) return NOTHING;
  const owner = !!meId && conversation.ownerId === meId;
  return { rename: owner, add: true, remove: owner, leave: true, dissolve: owner };
}

/** The server's sentence for a refusal, which it writes for a person. Nothing
 *  for a failure that never reached it. */
export function problemOf(err: unknown): string | undefined {
  return err instanceof ApiError ? err.message : undefined;
}

// ── New chat ────────────────────────────────────────────────────────────────

type Directory = Pick<RelayStore, 'me' | 'lookup'>;

export type Found = { person: Person } | { refusal: 'self' | 'missing' };

/**
 * Whoever the typed username or email address names, by exact lookup.
 *
 * Yourself is refused here, before anything is created: asking the server for a
 * conversation with only yourself in it is a 400, and it used to be asked.
 */
export async function findPerson(store: Directory, typed: string): Promise<Found> {
  const person = await store.lookup(typed.trim());
  if (!person) return { refusal: 'missing' };
  if (person.id === store.me()?.id) return { refusal: 'self' };
  return { person };
}

export type Started =
  | { id: string }
  | { refusal: 'empty' }
  | { refusal: 'unnamed' }
  /** `message` is the server's, and absent when the request never got there. */
  | { refusal: 'failed'; message?: string };

/**
 * One person makes a direct chat, or opens the one you already have: the
 * server does not look for it, so asking again would make a second. Two or
 * more make a group chat, which needs a name because the server requires one.
 * Never throws: a refusal is a result, so the dialog that asked cannot be left
 * waiting on a promise that rejected.
 */
export async function startChat(
  store: Pick<RelayStore, 'createConversation' | 'conversations'>,
  people: Person[],
  title: string
): Promise<Started> {
  if (people.length === 0) return { refusal: 'empty' };
  if (people.length === 1) {
    const id = people[0]!.id;
    const held = store.conversations().find((c) => c.kind === 'direct' && !c.official && c.members.includes(id));
    if (held) return { id: held.id };
  }
  const kind: Exclude<ConversationKind, 'self'> = people.length === 1 ? 'direct' : 'group';
  const name = kind === 'direct' ? people[0]!.name : title.trim();
  if (!name) return { refusal: 'unnamed' };
  try {
    return { id: await store.createConversation(kind, name, people.map((p) => p.id)) };
  } catch (err) {
    const message = problemOf(err);
    return message ? { refusal: 'failed', message } : { refusal: 'failed' };
  }
}
